import { useMemo } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { layoutsForAllCasesState, DEFAULT_LAYOUT } from './viewerLoaderState';
import { setUserLayoutForWindow, setUserLayoutsForCase } from './viewerLayoutWriters';
import type { Layout, Layouts } from './viewerLoaderState';
import { useViewerMetadata } from './useViewerMetadata';
import { useHangingProtocol } from 'domains/viewer/HangingProtocol/useHangingProtocol';
import { EMPTY } from 'config/constants';
import pick from 'ramda/es/pick';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { isPreviewingHangingProtocolState } from '../../HangingProtocol/state';
import { useClinicalStepMode } from '../../HangingProtocol/hooks/useClinicalStepMode';
import { WithApolloContext } from 'modules/Apollo/types';

type UseLayouts = {
  layoutForWindow: Nullable<Layout>;
  layoutsForCase: Layouts;
  setLayoutsForCase: (arg1: Layouts) => void;
  setLayoutForWindow: (arg1: Layout) => void;
  loading: boolean;
};

export const useLayouts = ({ context }: WithApolloContext): UseLayouts => {
  const allLayouts = useRecoilValue(layoutsForAllCasesState);
  const isPreviewingHangingProtocol = useRecoilValue(isPreviewingHangingProtocolState);
  const { openWindowsIds, windowId } = useViewerMetadata();
  const currentCaseId = useCurrentCaseId();
  const { hangingProtocolLayouts, loadingHangingProtocol } = useHangingProtocol({ context });
  const { isClinicalStepMode } = useClinicalStepMode();

  /**
   * The user's manual layout override for this case, when it applies at all.
   *
   * It does not apply in clinical step mode. `layoutsForAllCasesState` predates stepped HPs and
   * means "the layout the user picked for this case" -- which is not a coherent idea once layout is
   * a per-step property, because stepping changes neither the case nor the window. Ranked above the
   * step-derived layer (see the merge below) a single leftover entry pins every step to one grid.
   * That is the defect found and fixed four separate times, once per call site the ticket happened
   * to travel through: VX-4624, VX-4732, VX-4768, VX-4772.
   *
   * The write side already avoids it -- a layout picked in step mode is routed to `writeLayoutToStep`
   * (see `Viewer/index.tsx`), never here. This is the read side. Ignoring the layer outright means a
   * *stale* entry cannot win either: one persisted from a session before the protocol gained steps,
   * or left behind by a path that forgot to clear it. The `clearUserLayoutOverride` calls scattered
   * through the step operations become belt-and-braces rather than the only thing standing between a
   * reader and a wrong grid.
   *
   * Outside step mode nothing changes: the override is the user's own choice and still outranks the
   * protocol.
   */
  const currentLayoutsForCase: Layouts =
    !isPreviewingHangingProtocol && !isClinicalStepMode && currentCaseId != null
      ? allLayouts[currentCaseId]
      : EMPTY.OBJECT;

  // Both setters record a layout the *user* picked, so they write only the override layer. See
  // viewerLayoutWriters for why protocol-driven layout changes must not go through here.
  const setLayoutsForCase = useRecoilCallback(
    ({ set }) =>
      (updatedLayouts: Layouts) => {
        setUserLayoutsForCase(set, { caseId: currentCaseId, layouts: updatedLayouts });
      },
    [currentCaseId]
  );

  const setLayoutForWindow = useRecoilCallback(
    ({ set }) =>
      (layout: Layout) => {
        setUserLayoutForWindow(set, { caseId: currentCaseId, windowId, layout });
      },
    [currentCaseId, windowId]
  );

  // Filter out layouts for any windows which are not currently open.
  // This lets us 'save' the previously used layout and also give an accurate current layout.
  //
  // The window this viewer is rendering in always counts as open, whatever openWindowsIds says.
  // That list comes from the Workspace Manager extension, and there is a reachable state where it
  // comes back without this window: the extension reports windows whose names don't map to a window
  // id, so computeRawOpenTabsInfo's loop yields nothing and the artificial-tab fallback never fires
  // (it only covers there being no extension state at all). Without this, `pick` dropped the current
  // window, layoutForWindow came back undefined, and useRenderedViewportIdsForWindow's
  // `layout ?? [0, 0]` painted an empty reading area -- no error, no log, every thumbnail loaded and
  // not one viewport (VX-4764).
  const visibleWindowIds: ReadonlyArray<string> = useMemo(
    () =>
      windowId != null && !openWindowsIds.includes(windowId)
        ? [...openWindowsIds, windowId]
        : openWindowsIds,
    [openWindowsIds, windowId]
  );

  const layoutsForCase: Layouts = useMemo(() => {
    const defaultLayouts: Layouts = Object.fromEntries(
      visibleWindowIds.map((windowId) => [windowId, DEFAULT_LAYOUT])
    );

    return pick(visibleWindowIds, {
      ...defaultLayouts,
      ...hangingProtocolLayouts,
      ...currentLayoutsForCase,
    });
  }, [currentLayoutsForCase, hangingProtocolLayouts, visibleWindowIds]);

  // @ts-expect-error [EN-10263] - TS2538 - Type 'null' cannot be used as an index type. | TS2538 - Type 'undefined' cannot be used as an index type.
  const layoutForWindow = layoutsForCase[windowId];

  return {
    layoutForWindow,
    setLayoutForWindow,
    layoutsForCase,
    setLayoutsForCase,
    loading: loadingHangingProtocol,
  };
};
